/* eslint-disable no-use-before-define */
import {
    ExtensionCategories,
    ComponentTypes,
    ExtensionAvailabilities,
    TranslatableObject,
    ShareToMetadata,
    BroadcastMetadata,
} from 'lumapps-sdk-js';

import { ServerListResponse } from '@lumapps/base-api';

export {
    ComponentTypes,
    ExtensionSearchInput,
    ExtensionSearchOutput,
    ExtensionSearchResult,
    RESULT_STATUS,
    SEARCH_SDK_SUPPORTED_VERSIONS,
    SearchExtension,
    ExtensionCategories,
    FilterOptions,
} from 'lumapps-sdk-js';

export enum ExtensionsFilter {
    Available = 'available',
    // Deprecated, use statuses = "live"
    Enabled = 'enabled',
    Installed = 'installed',
    Outdated = 'outdated',
}

export enum ComponentErrors {
    NETWORK = 'NETWORK_ERROR',
    NOT_INSTALLED = 'NOT_INSTALLED',
    UNKNOWN_EXTENSION = 'UNKNOWN_EXTENSION',
}

export enum ExtensionStatus {
    disabled = 'disabled',
    live = 'live',
    unavailable = 'unavailable',
}

export type ExtensionIdParameter = Record<'extensionId', VersionedExtensionAPIProps['id']>;

export type LinksParameter = Record<'documentation', string>;

export type ExtensionVersionIdParameter = Record<'versionId', string>;

export type PartnerIdParameter = Record<'partnerId', PartnerAPIProps['id']>;

export type ComponentTypeParameter = Record<'componentType', ComponentTypes>;

export interface InstalledExtensionSearch {
    search?: string;
    categories?: ExtensionCategories[];
    enabled?: boolean;
    installedAfter?: string;
    installedBefore?: string;
}

export interface ComponentProps {
    areSettingsLoading: boolean;
    component: Record<string, any> | undefined;
    error: ComponentErrors | null;
    isComponentLoading: boolean;
    settingsError: boolean;
    type: ComponentTypes;
}

export interface ExtensionAPIProps {
    /** The category of the extension */
    category: ExtensionCategories;
    /** List of available component types for this extension */
    components: ComponentTypes[];
    /** Description of the extension */
    description: TranslatableObject;
    /** URL of extension's icon */
    icon: TranslatableObject;
    /** Identifier of the extension */
    readonly id: string;
    /** URL of extension's icon */
    links: LinksParameter;
    /** Name of the extension */
    name: TranslatableObject;
    /** Whether the extension requires an oauth connector */
    oauth: boolean;
    /** Identifier of the partner */
    readonly partnerId: string;
    /** Whether the extension is available for all organizations */
    public: boolean;
    /** Metadata information for specific extension categories */
    metadata?: ShareToMetadata | BroadcastMetadata;
    /** Extension global and internal settings */
    settings: {
        global: Record<string, unknown>;
        internal: Record<string, unknown>;
    };
    useConnectorSelector: boolean;
}

export interface ExtensionVersionProps {
    /** URL of the bundle */
    bundle: string | null;
    /** List of available component types for this extension */
    components: ComponentTypes[];
    /** The version's creation date */
    createdAt: string;
    /** Identifier of the extension */
    readonly extensionId?: string;
    /** Identifier of the version */
    id: string;
    /** Is compatible with mobile application */
    isMobileCompatible: boolean;
    /** The version's number following X.Y.Z */
    number: string;
    /** List of provider types */
    providerTypes: string[];
}

export interface VersionedExtensionAPIProps extends ExtensionAPIProps {
    version?: ExtensionVersionProps;
}

export interface ExtensionProps extends VersionedExtensionAPIProps {
    /** Whether the extension is enabled or not. */
    enabled: boolean;
    /** List of selected connectors */
    connectorIds?: Record<string, string | null>;
    /** Version of the installed extension. */
    installed?: ExtensionVersionProps;
    /** Date of installation of the extension. */
    installedAt: string | null;
    /** ID of the user who installed the extension. */
    installedBy: string | null;
    /** Whether we are loading or updating the extension. */
    loading: boolean;
    /** Whether the extension is unavailable. */
    status: ExtensionStatus;
    /** The last upgrade date of the extension. */
    upgradedAt?: string;
    /** Whether the extension is up-to-date or not. */
    isUpToDate: boolean;
    /** Whether to display the connector picker in widget's settings */
    useConnectorSelector: boolean;
}

export interface PartnerContactAPIProps {
    /** Contact e-mail */
    email: string;
    /** Support e-mail */
    supportEmail: string;
    /** Contact phone number */
    phone?: string;
    /** Contact website */
    website?: string;
}

export interface PartnerAPIProps {
    /** Bundle's URL for the configuration of the partner settings */
    bundle: string;
    /** Partner's contact information */
    contact: PartnerContactAPIProps;
    /** Partner's description */
    description: TranslatableObject;
    /** Partner's icon */
    icon: TranslatableObject;
    /** Partner's id */
    readonly id: string;
    /** Partner's name */
    name: TranslatableObject;
}

export interface PartnerProps extends PartnerAPIProps {
    loading: boolean;
    error: boolean;
}

export interface ListAPIProps<T> {
    /** Extension list cursor */
    cursor: string | undefined;
    /** Extension list */
    items: T[];
    /** Whether there is more extensions. Should be set to undefined if unknown (before first request) */
    more: boolean | undefined;
}

export interface ListProps<T> extends ListAPIProps<T> {
    /** Whether we are loading more results */
    loading: boolean | null;
}

export type InstalledExtensionsWithComponentsProps = Record<
    InstalledExtensionWithComponentsProps['id'],
    InstalledExtensionWithComponentsProps
>;

export type PartnersListProps = Record<PartnerProps['id'], PartnerProps>;

export type InstalledExtensionWithComponentsProps = Omit<ExtensionProps, 'components'> &
    Record<'components', ComponentProps[]>;

export interface NormalizedInstalledExtensionsAPIProps {
    entities: { extensions: InstalledExtensionsWithComponentsProps };
    result: ListAPIProps<InstalledExtensionWithComponentsProps['id']>;
}

// Same as NormalizedInstalledExtensionsAPIProps for now, but may change one day
// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface NormalizedEnabledExtensionsAPIProps extends NormalizedInstalledExtensionsAPIProps {}

export interface PlatformExtensionsSettingsProps {
    extensionSetup: {
        open: boolean;
        extensionId: InstalledExtensionWithComponentsProps['id'] | undefined;
    };
    partners: ListProps<PartnerProps['id']>;
    partnerSetup: {
        open: boolean;
        partnerId: PartnerProps['id'] | undefined;
    };
}

/**
 * Marketplace extension library props.
 */
export type MarketplaceExtensionLibraryProps = {
    cursor?: string;
    extensions: ExtensionProps[];
    loading: boolean;
    more?: boolean;
    search: string;
};

export type InstalledExtensionsProps = {
    apiCallKey?: string;
    cursor?: string;
    items: InstalledExtensionsWithComponentsProps;
    loading: boolean;
    more?: boolean;
    search?: InstalledExtensionSearch;
};

export interface MarketplaceProps {
    extensions: InstalledExtensionsProps;
    outdatedExtensions: InstalledExtensionsProps;
    partners: PartnersListProps;
    platformExtensionsSettings: PlatformExtensionsSettingsProps;
    // Marketplace
    marketplace: MarketplaceExtensionLibraryProps;
}

export type ExtensionsAPIProps = ListAPIProps<ExtensionProps>;
export type InstalledExtensionsAPIProps = ListAPIProps<InstalledExtensionAPIProps>;

/**
 * Micro-app's topics type
 * Specs might change, thus type too
 */
export type ExtensionTopic = string;

export type InstalledExtensionTopicsAPIProps = ServerListResponse<ExtensionTopic>;

export interface InstalledExtensionMedias {
    previewImage?: string;
}

/* Installed extension new format */
export interface InstalledExtensionProps {
    availability: ExtensionAvailabilities;
    category: ExtensionCategories;
    components: ComponentTypes[];
    description: TranslatableObject;
    icon: TranslatableObject;
    id: string;
    links: LinksParameter;
    name: TranslatableObject;
    partnerId: string;
    public: boolean;
    metadata?: ShareToMetadata | BroadcastMetadata;
    topics?: ExtensionTopic[];
    medias?: InstalledExtensionMedias;
    // Deprecated
    version?: ExtensionVersionProps;
    whitelist?: string[];
    useConnectorSelector: boolean;
}

export interface InstalledExtensionAPIProps {
    configuration: Record<string, unknown>;
    /** List of selected connectors */
    connectorIds?: Record<string, string | null>;
    createdAt: string;
    createdBy: string;
    extension: InstalledExtensionProps;
    readonly id: string;
    status: ExtensionStatus;
    updatedAt: string;
    updatedBy: string;
    upgradedAt: string;
    version: ExtensionVersionProps;
    isUpToDate: boolean;
}
